import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  fetchTaskLabelGroupCategory,
  fetchTaskLabelSetting,
  updateTaskLabelSettingLabelGroup,
} from '../../../../states/containers/task/taskSlice'
import { LangValue } from '../../../../states/types/common'
import { RequestGetTaskLabelSetting, ResponseGetTaskLabelSetting } from '../../../../states/types/task'
import { getGetPrams, setPath } from '../../../../utils/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import LabelGroupModule from '../../../components/task/LabelGroupModule'
import SearchConditionModule from '../../../components/task/SearchConditionModule'
import COLOR_LIST from '../../../constants/taskColor'
import { URL } from '../../../constants/url'
import CreateProjectModal from '../../../components/task/CreateProjectModal'
import { useAlert } from 'react-alert'
import VideoGuide from '../../../components/common/VideoGuide'
import { getTaskLabelSetting } from '../../../../states/apis/task'
import LabelGroupForCategoryModule from '../../../components/task/LabelGroupForCategoryModule'
import GroupButton from '../../../components/common/GroupButton'
import ImportProjectModal from '../../../components/task/ImportProjectModal'
import { updateTaskLabelSearchQuery, updateTaskLabelSettingCategoryView } from '../../../../states/containers/common/currentSlice'

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

interface GetParams {
  team_ids: string
  member_ids: string
  keywords: string
  disabled: string
  key: string
  direction: string
  page: string
  colors: string
  creator_member_ids: string
  viewer_member_ids: string
  user_member_ids: string
}

interface LabelGroup {
  id: number // ラベルグループID
  name: string // ラベルグループ名
  code: string | null
  color: string
  disabled: boolean // 非活性フラグ
  scheduled_start_date: string | null
  scheduled_end_date: string | null
  report_view: boolean
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: { // ラベル
    id: number // ラベルID
    label_master_id: number
    name: string // ラベル名
    code: string | null
    color: string // 色
    auto_labelings: { // オートラベリング
      id: number | null
      services: string[]
      keyword: {
        operator: string | null
        words: string[]
      }
      file_paths: string[]
      url: {
        pattern: string | null
        words: string[]
      }
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }[]
    disabled: boolean // 非活性フラグ
  }[]
  auto_budgets: {
    start_date: string
    end_date: string | null
    budget: number
  }[]
  auto_other_costs: {
    start_date: string
    end_date: string | null
    name: string
    cost: number
  }[]
  auto_scheduled_times: {
    start_date: string
    end_date: string | null
    time: number
  }[]
  created_at: string
  created_by: string | null
}

interface LabelGroupCategory {
  id: number | null
  category_name: string
  color: string
  parent_id: number | null
  label_groups: LabelGroup[]
  page_count: number
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const Index: FC = () => {
  const {
    task,
    login,
    current,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const alert = useAlert()

  const DISPLAY_LIMIT = 10
  const DISPLAY_LIMIT_FOR_CATEGORY = 5

  const [pageOffset, setPageOffset] = useState<number>(1)
  const [groupList, setGroupList] = useState<LabelGroup[] | null>(null)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [keywords, setKeywords] = useState<string[]>([])
  const [key, setKey] = useState<string>(login.permissions?.high_volume ? "id" : "order_index")
  const [direction, setDirection] = useState<string>(login.permissions?.high_volume ? "desc" : "asc")

  const [colorLabels, setColorLabels] = useState<string[]>([])
  const [creatorMemberIds, setCreatorMemberIds] = useState<number[]>([])
  const [viewerMemberIds, setViewerMemberIds] = useState<number[]>([])
  const [userMemberIds, setUserMemberIds] = useState<number[]>([])

  const [isOpenCreateProjectModal, setIsOpenCreateProjectModal] = useState<boolean>(false)

  const [labelGroupCategories, setLabelGroupCategories] = useState<LabelGroupCategory[]>([])

  const [openLabelGroupCategoryIds, setOpenLabelGroupCategoryIds] = useState<(number | null)[]>([])
  const [loadingLabelGroupCategoryIds, setLoadingLabelGroupCategoryIds] = useState<(number | null)[]>([])

  const [displayMode, setDisplayMode] = useState<string>("all")

  const [selectParentId, setSelectParentId] = useState<number | null>(null)
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false)

  const [fetchTrigger, setFetchTrigger] = useState<string>("")
  const [loadTrigger, setLoadTrigger] = useState<boolean>(false)

  const [pollingTrigger, setPollingTrigger] = useState<string>("")

  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true)
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false)
  const [isStop, setIsStop] = useState<boolean>(false)

  const [isDataLoad, setIsDataLoad] = useState<boolean>(false)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = () => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (!isStop && !isLoadingList && (bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setIsDataLoad(true)
    }
  }

  // confirmation modal
  const openConfirmationModal = (type: string, labelGroupId: number, labelId: number) => {
    let callback = () => {
      // none
    }
    let name = ""
    switch (type) {
      case "delete":
        if (labelId) {
          name = groupList?.find((v) => v.id === labelGroupId)?.labels.find((v) => v.id === labelId)?.name || ""
        } else {
          callback = () => {
            dispatch(
              updateTaskLabelSettingLabelGroup(
                {
                  id: labelGroupId.toString(),
                  disabled: true,
                  disable_status: "deleted",
                },
                () => {
                  alert.success("プロジェクトを削除しました！")
                  setGroupList((cur) => cur?.filter((v) => v.id !== labelGroupId) || [])
                },
              ),
            )
          }
          name = groupList?.find((v) => v.id === labelGroupId)?.name || ""
        }
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const changeDisableLabelGroup = (id: number, disabled: boolean) => {
    const param: {id :string, disabled: boolean, disable_status?: "deleted" | "completed"} = {
      id: id.toString(),
      disabled: false,
    }
    if (disabled) {
      param.disabled = true
      param.disable_status = "completed"
    }
    dispatch(
      updateTaskLabelSettingLabelGroup(
        param,
        () => {
          setGroupList((cur) => cur?.map((v) => (v.id === id ? { ...v, disabled: disabled } : v)) || [])
        },
      ),
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clickCreator = (state: {detail?: TemplateDetail | null, labelGroupId?: number}) => {
    if (displayMode === "all") {
      history.push({
        pathname: `${setPath(URL.TASK_LABEL_SETTING_CREATE.path, language)}`,
        state: state,
      })
    } else {
      history.push({
        pathname: `${setPath(URL.TASK_LABEL_SETTING_CREATE.path, language)}`,
        state: { ...state, parentId: selectParentId },
      })
    }
  }

  const createProject = (parentId?: number | null) => {
    if (parentId) {
      setSelectParentId(parentId)
    }
    setIsOpenCreateProjectModal(true)
  }

  const clickCopy = (labelGroupId: number) => {
    history.push({
      pathname: `${setPath(URL.TASK_LABEL_SETTING_CREATE.path, "ja")}`,
      state: { labelGroupId: labelGroupId },
    })
  }

  const getChildrenIds = (parentId: number | null): (number | null)[] => {
    const idList = labelGroupCategories.filter((v) => v.parent_id === parentId && v.id !== null).map((v) => v.id)
    if (!idList) return []
    return [...idList, ...idList.map((v) => getChildrenIds(v)).flat()]
  }

  const isExistLabelGroupCategoryId = (id: number | null) => openLabelGroupCategoryIds.includes(id)

  const toggleLabelGroupCategoryId = (id: number | null) => {
    if (isExistLabelGroupCategoryId(id)) {
      const closeIds = [id, ...getChildrenIds(id)]
      setOpenLabelGroupCategoryIds(openLabelGroupCategoryIds.filter((v) => !closeIds.includes(v)))
      setLabelGroupCategories(
        labelGroupCategories.map((v) => {
          if (v.id === id) {
            return ({
              ...v,
              label_groups: [],
              page_count: 0,
            })
          }
          return v
        }),
      )
    } else {
      setOpenLabelGroupCategoryIds([...openLabelGroupCategoryIds, id])
    }
  }

  const changePageOffset = (id: number | null, offset: number) => {
    const params: RequestGetTaskLabelSetting = {
      page: offset,
      limit: DISPLAY_LIMIT_FOR_CATEGORY,
      disabled: false,
      label_group_category_id: id,
    }
    setOpenLabelGroupCategoryIds([...openLabelGroupCategoryIds, id])
    setLoadingLabelGroupCategoryIds([...loadingLabelGroupCategoryIds, id])
    getTaskLabelSetting(params)
      .then((result: ResponseGetTaskLabelSetting) => {
        setLabelGroupCategories(
          labelGroupCategories.map((v) => {
            if (v.id === id) {
              return ({
                ...v,
                label_groups: [...v.label_groups, ...result.label_groups],
                page_count: result.page_count,
              })
            }
            return v
          }),
        )
        setLoadingLabelGroupCategoryIds(loadingLabelGroupCategoryIds.filter((v) => v !== id))
      })
  }

  const buildCategoryList = (level = 0, parent_id?: number | null, rules?: boolean[]):JSX.Element => {
    let list = []
    const levelCount = level + 1
    if (parent_id === 0) return (<></>)
    if (parent_id) {
      list = labelGroupCategories.filter((v) => v.parent_id === parent_id)
    } else {
      list = labelGroupCategories.filter((v) => v.parent_id === null)
    }
    if (!list.length) return (<></>)
    return (
      <>
        {
          list.map((v, i) => {
            const ruleList = [...rules?.map((rule, index) => (rules.length === index + 1 ? !rule : rule)) || [], list.length === i + 1]
            return (
              <React.Fragment key={`project_category_row_${v.category_name}`}>
                <div className={`project_category_row ${isExistLabelGroupCategoryId(v.id) ? "open" : "close"}`}>
                  <div className="project_category_header">
                    <div
                      className="title"
                      onClick={() => toggleLabelGroupCategoryId(v.id)}
                      role="none"
                    >
                      {
                        ruleList.map((w, j) => {
                          if (!j) return (<React.Fragment key={`${v.category_name}_${j + 0}_${level}`} />)
                          return (
                            <div
                              key={`${v.category_name}_${j + 0}_${level}`}
                              style={{
                                width: "14px",
                              }}
                            >
                              {
                                ruleList.length === j + 1 ? (
                                  <>
                                    {w ? (<>┗</>) : (<>┣</>)}
                                  </>
                                ) : (
                                  <>
                                    {w && (<>┃</>)}
                                  </>
                                )
                              }
                            </div>
                          )
                        })
                      }
                      <FontAwesomeIcon icon={['fas', isExistLabelGroupCategoryId(v.id) ? 'angle-down' : 'angle-right']} className="icon" />
                      <FontAwesomeIcon
                        icon={['fas', isExistLabelGroupCategoryId(v.id) ? 'folder-open' : 'folder']}
                        className="icon"
                        style={{
                          color: v.color,
                        }}
                      />
                      {v.category_name}
                      <div className="open_folder_button">
                        {isExistLabelGroupCategoryId(v.id) ? "閉じる" : "展開する"}
                      </div>
                    </div>
                  </div>
                  {
                    isExistLabelGroupCategoryId(v.id) && (
                      <div style={{ marginLeft: `${(ruleList.length - 1) * 22}px` }}>
                        <LabelGroupForCategoryModule
                          labelGroupCategory={v}
                          isLoading={loadingLabelGroupCategoryIds.includes(v.id)}
                          deleteLabelGroupCallback={(labelGroupId) => openConfirmationModal("delete", labelGroupId, 0)}
                          disableLabelGroupCallback={(labelGroupId, disable) => changeDisableLabelGroup(labelGroupId, disable)}
                          copyLabelGroupCallback={(labelGroupId) => clickCopy(labelGroupId)}
                          pageOffsetCallback={(offset) => changePageOffset(v.id, offset)}
                          teams={task.taskLabelSetting?.teams || []}
                          creatable={login.permissions?.task_project_setting}
                        />
                      </div>
                    )
                  }
                </div>
                {isExistLabelGroupCategoryId(v.id) && buildCategoryList(levelCount, v.id, ruleList)}
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const changeDisplayMode = (mode: string) => {
    setOpenLabelGroupCategoryIds([])
    setDisplayMode(mode)
    dispatch(updateTaskLabelSettingCategoryView(mode === "category"))
  }

  const clickImport = () => {
    setIsOpenImportModal(true)
    setIsOpenCreateProjectModal(false)
  }

  const addImport = () => {
    alert.success("プロジェクト一括登録を受け付けました")
    setFetchTrigger(uniqueId())
  }

  useEffect(() => {
    if (!task.taskLabelSetting?.label_groups.length) return
    setGroupList((cur) => ([...cur || [], ...task.taskLabelSetting?.label_groups || []]))
  }, [task.taskLabelSetting])

  useEffect(() => {
    let getParams = getGetPrams(location.search) as GetParams
    if (!location.search && current.taskLabelSearchQuery) {
      getParams = getGetPrams(current.taskLabelSearchQuery) as GetParams
    }

    if (getParams.disabled) {
      setIsDisabled(JSON.parse(getParams.disabled.toLowerCase()))
    }
    if (getParams.keywords) {
      setKeywords(decodeURIComponent(getParams.keywords).split(','))
    }
    if (getParams.key) {
      if (getParams.key === "order_index" && login.permissions?.high_volume) {
        setKey("id")
      } else {
        setKey(getParams.key)
      }
    }
    if (getParams.direction) {
      if (getParams.key === "order_index" && login.permissions?.high_volume) {
        setDirection("desc")
      } else {
        setDirection(getParams.direction)
      }
    }
    if (getParams.page) {
      setPageOffset(Number(getParams.page) || 1)
    }
    if (getParams.colors) {
      setColorLabels(getParams.colors.split(','))
    }
    if (getParams.creator_member_ids) {
      setCreatorMemberIds(getParams.creator_member_ids.split(',').map((v) => Number(v)))
    }
    if (getParams.viewer_member_ids) {
      setViewerMemberIds(getParams.viewer_member_ids.split(',').map((v) => Number(v)))
    }
    if (getParams.user_member_ids) {
      setUserMemberIds(getParams.user_member_ids.split(',').map((v) => Number(v)))
    }
    setLoadTrigger(true)
  }, [])

  useEffect(() => {
    if (!loadTrigger) {
      setIsLoadingFirst(false)
      setIsLoadingList(false)
      return
    }
    setIsLoadingFirst(true)
    const query = [`disabled=${isDisabled}`]
    const params: RequestGetTaskLabelSetting = {
      page: pageOffset,
      limit: DISPLAY_LIMIT,
      disabled: isDisabled,
    }
    if (isDisabled) {
      params.disable_status = "completed"
    }
    if (keywords.length) {
      params.keywords = keywords
      query.push(`keywords=${keywords}`)
    }
    if (key) {
      params.key = key
      query.push(`key=${key}`)
    }
    if (direction) {
      params.direction = direction
      query.push(`direction=${direction}`)
    }
    if (colorLabels.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => colorLabels.includes(v.label)).map((v) => v.color)
      query.push(`colors=${colorLabels}`)
    }
    if (creatorMemberIds.length) {
      params.creator_member_ids = creatorMemberIds
      query.push(`creator_member_ids=${creatorMemberIds}`)
    }
    if (viewerMemberIds.length) {
      params.viewer_member_ids = viewerMemberIds
      query.push(`viewer_member_ids=${viewerMemberIds}`)
    }
    if (userMemberIds.length) {
      params.user_member_ids = userMemberIds
      query.push(`user_member_ids=${userMemberIds}`)
    }
    setGroupList(null)
    dispatch(
      fetchTaskLabelSetting(
        params,
        (result) => {
          setIsLoadingFirst(false)
          setIsDataLoad(false)
          if (!result.label_groups.length) {
            setIsStop(true)
          }
        },
      ),
    )
    history.push(`${setPath(URL.TASK_LABEL_SETTING.path, language)}?${query.join('&')}`)
  }, [keywords, loadTrigger, isDisabled, key, direction, colorLabels, creatorMemberIds, viewerMemberIds, userMemberIds, fetchTrigger])

  useEffect(() => {
    if (!loadTrigger || pageOffset === 1) {
      setIsDataLoad(false)
      setIsLoadingList(false)
      return
    }
    const params: RequestGetTaskLabelSetting = {
      page: pageOffset,
      limit: DISPLAY_LIMIT,
      disabled: isDisabled,
    }
    if (isDisabled) {
      params.disable_status = "completed"
    }
    if (keywords.length) {
      params.keywords = keywords
    }
    if (key) {
      params.key = key
    }
    if (direction) {
      params.direction = direction
    }
    if (colorLabels.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => colorLabels.includes(v.label)).map((v) => v.color)
    }
    if (creatorMemberIds.length) {
      params.creator_member_ids = creatorMemberIds
    }
    if (viewerMemberIds.length) {
      params.viewer_member_ids = viewerMemberIds
    }
    if (userMemberIds.length) {
      params.user_member_ids = userMemberIds
    }
    dispatch(
      fetchTaskLabelSetting(
        params,
        (result) => {
          setIsDataLoad(false)
          setIsLoadingList(false)
          if (!result.label_groups.length) {
            setIsStop(true)
          }
        },
      ),
    )
  }, [pageOffset])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    dispatch(fetchTaskLabelGroupCategory())
  }, [])

  useEffect(() => {
    setLabelGroupCategories([
      {
        id: 0,
        category_name: "カテゴリーに属さないプロジェクト",
        color: "#DDDDDD",
        parent_id: null,
        label_groups: [],
        page_count: 0,
      },
      ...task.taskLabelGroupCategory?.label_group_categories.map((v) => ({
        id: v.id,
        category_name: v.category_name,
        color: v.color,
        parent_id: v.parent_id,
        label_groups: [],
        page_count: 0,
      })) || [],
    ])
  }, [task.taskLabelGroupCategory, displayMode])

  useEffect(() => {
    if (!location.search) return
    dispatch(updateTaskLabelSearchQuery(location.search))
  }, [location.search])

  useEffect(() => {
    let mode = "all"
    if (current.taskLabelSettingCategoryView) {
      mode = "category"
    }
    setDisplayMode(mode)
  }, [current.taskLabelSettingCategoryView])

  useEffect(() => {
    if (!pollingTrigger) return
    const polling = setTimeout(() => {
      setFetchTrigger(uniqueId())
    }, 10 * 1000)
    // eslint-disable-next-line consistent-return
    return () => {
      if (pollingTrigger) {
        clearTimeout(polling)
      }
    }
  }, [pollingTrigger])

  useEffect(() => {
    setPollingTrigger(!task.taskLabelSetting?.importing_project ? "" : uniqueId())
  }, [task.taskLabelSetting])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    if (isLoadingFirst || displayMode === "category" || isStop || !isDataLoad) {
      setIsDataLoad(false)
      return
    }
    setPageOffset(pageOffset + 1)
    setIsLoadingList(true)
  }, [isDataLoad])

  return (
    <section className="task_page task_status">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - プロジェクト設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        プロジェクト設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          プロジェクト設定
          <VideoGuide
            videoId="NGm_8YlftBI"
          />
        </h1>
        <div className="tools">
          {
            !pollingTrigger && login.permissions?.task_project_setting && (
              <div className="button_area">
                <GroupButton
                  list={[
                    {
                      icon: "plus",
                      text: "プロジェクトを新規作成する",
                      callback: createProject,
                    },
                    {
                      icon: "plus",
                      text: "プロジェクトを一括登録する",
                      callback: () => setIsOpenImportModal(true),
                    },
                  ]}
                />
              </div>
            )
          }
        </div>
      </section>
      <section className="page_description">
        <p>
          プロジェクトの新規作成、既存プロジェクトの編集、完了、削除をすることができます。
        </p>
        <p>
          プロジェクト表示メンバーは、プロジェクトレポートの参照、プロジェクト参加メンバーの仕分け状況確認などができます。
        </p>
      </section>
      {
        pollingTrigger ? (
          <>
            <LoadingSquare>
              <>
                プロジェクトインポート処理が実行中です。しばらくおまちください。
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            <section className="project_display_type_select_wrapper">
              表示タイプ：
              <button
                type="button"
                style={{ backgroundColor: "#3773bc" }}
                onClick={() => changeDisplayMode("all")}
              >
                プロジェクト一覧表示
              </button>
              <button
                type="button"
                style={{ backgroundImage: "linear-gradient(45deg, #343bca, #a278b6 50%, #f3c4aa)" }}
                onClick={() => changeDisplayMode("category")}
              >
                カテゴリー別表示
                <div className="pick_up">
                  オススメ
                </div>
              </button>
            </section>
            {
              displayMode === "all" && (
                <SearchConditionModule
                  name="プロジェクト設定"
                  keywords={keywords}
                  sortKey={key}
                  sortDirection={direction}
                  disabled={isDisabled}
                  colors={colorLabels}
                  creatorMemberIds={creatorMemberIds}
                  viewerMemberIds={viewerMemberIds}
                  userMemberIds={userMemberIds}
                  memberList={task.taskLabelSetting?.members.map((v) => ({ id: v.id, name: v.name }))}
                  callbackKeywords={setKeywords}
                  callbackKey={setKey}
                  callbackDirection={setDirection}
                  callbackDisabled={setIsDisabled}
                  callbackPageOffset={setPageOffset}
                  callbackColorLabels={setColorLabels}
                  callbackCreatorMemberIds={setCreatorMemberIds}
                  callbackViewerMemberIds={setViewerMemberIds}
                  callbackUserMemberIds={setUserMemberIds}
                />
              )
            }
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-detail">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "right",
                      gap: "4px",
                    }}
                  >
                    検索条件に該当したプロジェクト件数
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#008800",
                      }}
                    >
                      {task.taskLabelSetting?.project_count.toLocaleString() || 0}
                    </div>
                    件
                  </div>
                  {
                    displayMode === "all" && !pollingTrigger && login.permissions?.task_project_setting && (
                      <>
                        <div
                          className="create_project"
                          onClick={() => createProject()}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['fas', 'plus']} />
                          &nbsp;
                          プロジェクトを新規作成する
                        </div>
                      </>
                    )
                  }
                  {
                    isLoadingFirst ? (
                      <LoadingSquare>
                        <>
                          プロジェクトを読み込んでいます…
                        </>
                      </LoadingSquare>
                    ) : (
                      <>
                        {
                          displayMode === "all" && (
                            <>
                              {
                                groupList?.length ? (
                                  <>
                                    {
                                      groupList?.map((value) => (
                                        <LabelGroupModule
                                          key={uniqueId(`label_group_`)}
                                          list={value}
                                          deleteLabelGroupCallback={(labelGroupId) => openConfirmationModal("delete", labelGroupId, 0)}
                                          disableLabelGroupCallback={(labelGroupId, disable) => changeDisableLabelGroup(labelGroupId, disable)}
                                          copyLabelGroupCallback={(labelGroupId) => clickCopy(labelGroupId)}
                                          teams={task.taskLabelSetting?.teams || []}
                                          creatable={login.permissions?.task_project_setting}
                                        />
                                      ))
                                    }
                                  </>
                                ) : (
                                  <></>
                                )
                              }
                            </>
                          )
                        }
                        {
                          displayMode === "category" && (
                            <>
                              {
                                !!labelGroupCategories.length && buildCategoryList()
                              }
                            </>
                          )
                        }
                      </>
                    )
                  }
                  {
                    isLoadingList && (
                      <div className="block-detail">
                        <LoadingSquare>
                          <>
                            プロジェクトを読み込んでいます…
                          </>
                        </LoadingSquare>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        )
      }
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
      <CreateProjectModal
        closeCallback={() => setIsOpenCreateProjectModal(false)}
        addCallback={(state) => clickCreator(state)}
        importCallback={clickImport}
        isDisplay={isOpenCreateProjectModal}
      />
      <ImportProjectModal
        isDisplay={isOpenImportModal}
        dataList={task.taskLabelGroupImport}
        closeCallback={() => setIsOpenImportModal(false)}
        addCallback={() => addImport()}
        isCost={!!login.permissions?.task_project_cost_setting}
      />
    </section>
  )
}

export default Index
